import { SignatureV4 } from '@smithy/signature-v4';
import { HttpRequest } from '@smithy/protocol-http';
import { parseQueryString } from '@smithy/querystring-parser';

import { intoObject } from '../../util.js';

import type { FetchEnhancer } from '../../types/fetchEnhancer.js';
import type { Provider, AwsCredentialIdentity, ChecksumConstructor } from '@smithy/types';

export type CredentialsProvider = AwsCredentialIdentity | Provider<AwsCredentialIdentity>;

export type SigningEnhancerOptions = {
  /**
   * Configuration for the credentials used by the signing enhancer
   */
  awsCredentials: CredentialsProvider;
};

/**
 * Constructs a signing enhancer given a checksum constructor.
 */
export const signingEnhancerFactory =
  (hasher: ChecksumConstructor): FetchEnhancer<SigningEnhancerOptions> =>
  (fetch) =>
  async (info, options) => {
    if (!options) {
      throw new Error('No options provided');
    }
    if (!options?.awsCredentials) {
      throw new Error('No awsCredentials provided');
    }

    const signer = new SignatureV4({
      applyChecksum: false, // `x-amz-content-sha256` checksum header is not a requirement for API Gateway
      credentials: options.awsCredentials,
      region: 'eu-west-1',
      service: 'execute-api',
      sha256: hasher,
    });

    const request = new Request(info, options);

    const url = new URL(request.url);

    // Consume and remove internal header before signing
    // Header is only used to pass data between enhancers
    // and should not be included in the request or signing
    const signingDate = request.headers.get('x-signing-date') ?? new Date();
    request.headers.delete('x-signing-date');

    const httpRequest = new HttpRequest({
      body: options?.body,
      headers: {
        ...intoObject(request.headers),
        host: url.host,
      },
      hostname: url.hostname,
      method: options.method?.toUpperCase() ?? 'GET',
      path: url.pathname,
      protocol: url.protocol,
      query: parseQueryString(url.search),
    });

    const signedRequest = await signer.sign(httpRequest, {
      signingDate,
    });

    delete signedRequest.headers.host;

    return fetch(url.toString(), {
      ...options,
      headers: signedRequest.headers,
    });
  };
